import {AfterContentChecked, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChildren} from '@angular/core';
import {animate, group, query, style, transition, trigger} from '@angular/animations';
import {UserService} from '../../../../services/user-services/user.service';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ProcessBoxComponent} from '../../../../pages/dashboard/process-box/process-box.component';
import {NgClass, NgFor, NgIf} from '@angular/common';
import {PlatformService} from '../../../../services/platform-service.service';
import {Subscription} from 'rxjs';
import {BookingCardComponent} from '../../../../pages/users/profile/profile-purchase/booking-card/booking-card.component';
import {Router} from '@angular/router';
import {
  HousingCertificateCardComponent
} from "../../../../pages/users/profile/profile-purchase/housing-certificate-card/housing-certificate-card.component";

//----------------------------------------------------------------------------------------------------
const PossibleMode = {
  slider: 'slider' as 'slider',
  modal: 'modal' as 'modal',
  header: 'header' as 'header',
  payout: 'payout' as 'payout',
  certificate:'certificate'as 'certificate',
};
type PossibleMode = (typeof PossibleMode)[keyof typeof PossibleMode];
export {PossibleMode};

//----------------------------------------------------------------------------------------------------
const ButtonPosition = {
  tr: 'top_right' as 'top_right',
  br: 'bottom_right' as 'bottom_right',
  mlr: 'mid-left_right' as 'mid-left_right',
}
type ButtonPosition = (typeof ButtonPosition)[keyof typeof ButtonPosition];
export {ButtonPosition};

//----------------------------------------------------------------------------------------------------
@Component({
  selector: 'app-slider',
  templateUrl: './slider.component.html',
  styleUrls: ['./slider.component.scss'],
  animations: [
    trigger('slider', [
      transition(':increment', group([
        query(':enter', [
          style({'margin-left': '0'}),
          animate('400ms ease-out', style({'margin-left': '0'}))
        ]),
        query(':leave', [
          animate('400ms ease-out', style({'margin-left': '-{{marginWidth}}px'}))
        ], {optional: true})
      ])),
      transition(':decrement', group([
        query(':enter', [
          style({
            'margin-left': '-{{marginWidth}}px'
          }),
          animate('400ms ease-out', style({'margin-left': '0'}))
        ]),
        query(':leave', [
          animate('400ms ease-out', style({'margin-left': '0'}))
        ], {optional: true})
      ]))
    ]),
    trigger('headerSlider', [
      transition(':increment', group([
        query(':enter', [
          style({'left': '100%'}),
          animate('400ms ease-in-out', style({'left': '0'}))
        ]),
        query(':leave', [
          animate('400ms ease-in-out', style({'left': '-100%'}))
        ])
      ])),
      transition(':decrement', group([
        query(':enter', [
          style({'left': '-100%'}),
          animate('400ms ease-in-out', style({'left': '0'}))
        ]),
        query(':leave', [
          animate('400ms ease-in-out', style({'left': '100%'}))
        ])
      ]))
    ]),
  ],
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    NgFor,
    ProcessBoxComponent,
    TranslateModule,
    BookingCardComponent,
    HousingCertificateCardComponent,
  ]
})
export class SliderComponent implements OnInit, AfterContentChecked, OnDestroy {

  //Required. Permet de savoir quel affichage & quelles fonctionnalités doivent être utilisées. Défaut "slider".
  @Input() public mode: PossibleMode = 'slider';

  //Required. la liste complète des items qui vont devoir être slider. Défaut : []
  @Input() public items: any[] = [];

  //Optionnel. Utilisé pour le mode header. Détermine si le slider doit fonctionné tout seul. Défaut : false.
  @Input() private _isAutomatic: boolean = false;

  //Optionnel. Utilisé pour le mode header. Détermine au bout de combien de temps (s) le slider automatique doit changer d'image. Défaut : 4500
  @Input() private _automaticDelay: number = 4500;

  //Optionnel. Utilisé pour le mode header. Détermine s'il faut afficher ou non une barre de progression avant que ça change d'image. Défaut : false
  @Input() public showAutomaticProgress: boolean = false;

  //Optionnel. Utilisé pour le mode header. Détermine si le header est infini (revient à la slide 1 quand slide max atteinte)
  @Input() public isInfiniteLoop: boolean = false;

  //Required si mode = slider. Détermine le nombre d'élément à afficher. Défaut : 3
  @Input() public numberOfDisplayedItems: number = 3;

  //Optionnel. Le nombre maximal d'élément que le user va pouvoir voir en slidant. Défaut : 5.
  @Input() public maximumOfItems: number = 5;

  //Optionnel. Permet de changer l'emplacement des boutons selon l'affichage souhaité. Défaut : "top_right"
  @Input() public buttonPosition: ButtonPosition = 'top_right';

  //Optionnel. Permet d'assigner des classes directement sur les boutons. Défaut : ''. Exemple : 'text-danger, font-28, bg-grey'
  @Input() public buttonClass: string = '';

   //Optionnel. Détermine s'il faut afficher un titre de catégorie
  @Input() public hasTitle:boolean = true;
   //Optionnel. Détermine s'il faut afficher un titre de catégorie
  @Input() public itemLayout:string;

   //Required si hasTitle = true. Clef de traduction du titre.
  @Input() public title:string;

  @ViewChildren('process_box') processBox;

  @Output() public pageChange: EventEmitter<string> = new EventEmitter<string>();


  // Récupère la liste des index à afficher dans le slider. Utilisé dans previousItem() & nextItem().
  public indexArray: number[] = [];
  //
  private interval$;
  //
  private progressInterval$;
  //
  private pauseInterval$;
  //
  public progressBarWidth: number = 0;

  //
  public animMargin:number = 326;

  // langue de l'utilisateur
  public language:string;
  private eventSubscription: Subscription;


  constructor(
    private userService: UserService,
    private translateService: TranslateService,
    private modalService: NgbModal,
    private platformService: PlatformService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.language = this.translateService.currentLang;
    if (window.innerWidth < 768) {
      this.numberOfDisplayedItems = 2;
    }

    if (this.mode !== 'slider') {
      if ((this.mode === 'payout' || this.mode == 'certificate') && window.innerWidth > 1150) {
        this.numberOfDisplayedItems = 2;
      } else {
        this.numberOfDisplayedItems = 1;
      }

    } else {
      if (!this.maximumOfItems || this.numberOfDisplayedItems == 2 || this.items.length < this.maximumOfItems) {
        this.maximumOfItems = this.items.length;
      }
    }
    this.indexArray = [...Array(this.numberOfDisplayedItems).keys()];
    if (this.mode === 'header') {
      this._isAutomatic = true;
      this.showAutomaticProgress = true;
      this.isInfiniteLoop = true;
      this.interval$ = setInterval(() => this.autoSlide(), this._automaticDelay);
      this.progressInterval$ = setInterval(() => this.progressBarUpdate(), this._automaticDelay / 100);
    }

    this.eventSubscription = this.platformService.eventEmitter.subscribe(data => {
      this.language = data;
    });
  }

  ngAfterContentChecked (): void {
    if (this.processBox && this.processBox.first) {
      this.animMargin = this.processBox.first.nativeElement.offsetWidth
    }
  }


  ngOnDestroy() {
    if (this.interval$) clearInterval(this.interval$);
    if (this.progressInterval$) clearInterval(this.progressInterval$);
    if (this.eventSubscription) {
      this.eventSubscription.unsubscribe();
    }
  }


  /**
   * On décrémente de 1 les index de indexArray.
   * Permet de revenir aux items précédents du slider.
   * Appliqué au bouton précédent.
   * Utilisé dans l'autoslider.
   */
  public previousItem():void {
    if (this.indexArray[0] > 0) {
      this.indexArray = this.indexArray.map(i => i - 1);
    } else if (this.isInfiniteLoop) {
      this.indexArray = [this.items.length - 1];
    }
  }

  /**
   * SOn incrémente de 1 les index de indexArray.
   * Permet de voir les items suivants du slider.
   * Appliqué au bouton suivant.
   * Utilisé dans l'autoslider.
   */
  public nextItem():void {
    if (!this.isInfiniteLoop) {
      if (this.indexArray[this.indexArray.length - 1] !== this.maximumOfItems - 1) {
        this.indexArray = this.indexArray.map(i => i + 1);
      } else {
        clearInterval(this.interval$);
      }
    } else {
      if (this.indexArray[this.indexArray.length - 1] !== this.items.length - 1) {
        this.indexArray = this.indexArray.map(i => i + 1);
      } else {
        this.indexArray = [0];
      }
    }
  }

  /**
   * Envoie l'utilisateur sur la page, procress ou le partenaire
   * @param link
   */
  public goTo(link): void {
    window.open(link, '_blank', 'noopener,noreferrer')
  }

  /**
   * Slide automatiquement et reset la progressbar
   *
   */
  private autoSlide(): void {
    this.nextItem();
    if (this.showAutomaticProgress) {
      this.progressBarWidth = 0;
    }
  }

  /**
   * Incrémente @progressBarWidth afin de créer la progress bar
   *
   */
  private progressBarUpdate() {
    this.progressBarWidth++;
    if (this.progressBarWidth > 100) {
      clearInterval(this.progressInterval$);
    }
  }

  /**
   * Met l'auto-slider en pause et reprend au bout de x ms
   */
  public pauseIntervals() {
    if (this.pauseInterval$) {
      clearTimeout(this.pauseInterval$);
    }
    if (this._isAutomatic) {
      this.progressBarWidth = 0;
      clearInterval(this.interval$);
      clearInterval(this.progressInterval$);
      this.pauseInterval$ = setTimeout(() => {
        this.interval$ = setInterval(() => this.autoSlide(), this._automaticDelay);
        this.progressInterval$ = setInterval(() => this.progressBarUpdate(), this._automaticDelay/100);
        clearTimeout(this.pauseInterval$);
      }, this._automaticDelay);
    }
  }

  /**
   * Améliore les performances.
   * Utilisé sur le *ngFor.
   * Aucune fonction pratique, pas besoin de le modifier.
   * @param item
   */
  public trackBy(item):void {
    return item.id;
  }

  public goToBrowse(): void {
    if (this.mode === 'payout' || this.mode == 'certificate') {
      this.router.navigate(['account/my-profile'], {queryParams: {tab: 'purchase'}});
    } else {
      this.pageChange.emit('explore');
    }
  }

  public dismissModal():void {
    this.modalService.dismissAll();
  }
}
